"use client"

import { useState } from "react"
import { m, AnimatePresence } from "framer-motion"
import { useTranslations } from "@/lib/intl/use-translations"
import { homeKeys as k } from "@/i18n/home-keys"
import Link from "next/link"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { ChevronDownIcon } from "@/components/ui/icons"
import { LpaGiftNote } from "@/app/(frontend)/[country]/[locale]/_components/membership-first/lpa-gift"

/** 9 hardcoded FAQ keys from the `home` namespace — shared across homepage + /go pages. */
export const HOMEPAGE_FAQ_KEYS = [
  { q: k.faqQ1, a: k.faqA1 },
  { q: k.faqQ2, a: k.faqA2 },
  { q: k.faqQ3, a: k.faqA3 },
  { q: k.faqQ4, a: k.faqA4 },
  { q: k.faqQ5, a: k.faqA5 },
  { q: k.faqQ6, a: k.faqA6 },
  { q: k.faqQ7, a: k.faqA7 },
  { q: k.faqQ8, a: k.faqA8 },
  { q: k.faqQ9, a: k.faqA9 },
] as const

export interface FaqItem {
  question: string
  answer: string
}

export interface FaqSectionProps {
  /** Override heading (default: home.faqHeading) */
  heading?: string
  /** Override the link label. Pass `null` to hide the link. (default: home.faqLink) */
  linkLabel?: string | null
  /** Override the link href (default: /faq). Ignored if linkLabel is null. */
  linkHref?: string
  /** Pre-translated FAQ items. If omitted, defaults to the homepage's home.faq* keys. */
  items?: FaqItem[]
  /** Allow multiple items to be expanded simultaneously (default: false). */
  allowMultiple?: boolean
}

/**
 * Two-column FAQ section — heading + optional "see all" link on the left,
 * accordion on the right. Defaults to the homepage's 9 shared `home.faq*`
 * questions; pages can pass their own `items` array (pre-translated) plus
 * an `allowMultiple` flag to let multiple items be open at once.
 */
export function FaqSection({
  heading,
  linkLabel,
  linkHref,
  items,
  allowMultiple = false,
}: FaqSectionProps = {}) {
  const t = useTranslations("home")
  const lp = useLocalePath()
  const [openIndices, setOpenIndices] = useState<Set<number>>(new Set())

  const resolvedHeading = heading ?? t(k.faqHeading)
  const resolvedLinkLabel = linkLabel === undefined ? t(k.faqLink) : linkLabel
  const resolvedLinkHref = linkHref ?? lp("/faq")
  const resolvedItems: FaqItem[] =
    items ??
    HOMEPAGE_FAQ_KEYS.map((entry) => ({
      question: t(entry.q),
      answer: t(entry.a),
    }))

  function toggle(index: number) {
    setOpenIndices((prev) => {
      const next = new Set(allowMultiple ? prev : [])
      if (prev.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }

  return (
    <section className="lazy-section py-16 md:py-24">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 px-4 md:grid-cols-[1fr_2fr] md:px-10">
        {/* Left column */}
        <div>
          <div>
            <h2 className="type-display-sm text-neutral-900">
              {resolvedHeading}
            </h2>
            {resolvedLinkLabel && (
              <Link
                href={resolvedLinkHref}
                className="border-aubergine-800/30 text-aubergine-800 hover:bg-aubergine-800 hover:border-aubergine-800 type-title-sm mt-6 flex w-full items-center justify-center gap-1.5 rounded-full border px-5 py-2.5 transition-colors hover:text-white md:inline-flex md:w-auto"
              >
                {resolvedLinkLabel} →
              </Link>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="divide-y divide-neutral-200">
          {resolvedItems.map((item, index) => {
            const isOpen = openIndices.has(index)
            return (
              <div key={index}>
                <button
                  className="flex w-full cursor-pointer items-center justify-between py-4 text-left"
                  onClick={() => toggle(index)}
                  aria-expanded={isOpen}
                >
                  <span className="type-title-md text-neutral-900">
                    {item.question}
                  </span>
                  <ChevronDownIcon
                    className={`ml-4 flex-shrink-0 text-neutral-400 transition-transform duration-200 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {/* safe-animate-presence: conditional-mount — gated by `isOpen=true` */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <m.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      className="overflow-hidden"
                      role="region"
                    >
                      <p className="type-body-lg pb-4 text-neutral-600">
                        {item.answer}
                      </p>
                    </m.div>
                  )}
                </AnimatePresence>
              </div>
            )
          })}
        </div>
        {/* The free Lp(a)'s small print, under the questions (lpa-gift). */}
        <LpaGiftNote place="faq" className="md:col-span-2" />
      </div>
    </section>
  )
}
